import * as React from 'react';

import { PaywallScreens } from '~analytics/types';
import { translate } from '~l10n/client';
import { Container } from '~shared/components/Container';
import { xAgoFormatter } from '~shared/components/helpers';
import { IconDocument, IconFolder } from '~shared/components/icons/standard';
import { Spinner } from '~shared/components/Spinner/Spinner';
import { sortByProperty } from '~shared/components/Table/sortingFns/sortByProperty/sortByProperty';
import { usePaywallReducer } from '~teacher/components/PaywallDialog';
import {
  PurgeDialog,
  usePurgeDialog,
} from '~teacher/components/quizzes/PurgeDialog';
import { SharedLibraryPaywall } from '~teacher/components/SharedLibraryPaywall';
import { OfficialLibraryBadge } from '~teacher/components/workspaces/OfficialLibraryBadge';
import { usePurgeWorkspaceItemAction } from '~teacher/components/workspaces/WorkspaceDeleted/usePurgeWorkspaceItemAction';
import { useRestoreWorkspaceItemAction } from '~teacher/components/workspaces/WorkspaceDeleted/useRestoreWorkspaceItemAction';
import { useHandleWorkspaceItemSelection } from '~teacher/components/workspaces/WorkspaceQuizzes/hooks/useHandleWorkspaceItemSelection';
import {
  WorkspaceItemTable,
  useParentIdFilter,
} from '~teacher/components/workspaces/WorkspaceQuizzes/WorkspaceItemTable';
import { WorkspaceQuizzesBreadcrumbs } from '~teacher/components/workspaces/WorkspaceQuizzes/WorkspaceQuizzesBreadcrumbs';
import { useSocrativeRouter } from '~teacher/context/RouterContext';
import { useWorkspacePermissionsV2 } from '~teacher/hook/workspaces';
import {
  WorkspaceItem,
  useWorkspaceItems,
} from '~teacher/hook/workspaces/useWorkspaceItems';

import { WorkspaceRole } from '../../../../api/workspaces/getWorkspaceMembers';

interface Props {
  workspaceId: string;
  // Parent folder id
  itemId?: string;
}

const columns = [
  {
    id: 'type',
    accessor: (item) => item.type,
    customWidth: '60px',
    disableSortBy: true,
    Cell: ({ value }: { value: any }) =>
      value === 'quiz' ? <IconDocument /> : <IconFolder />,
  },
  {
    Header: translate('Name'),
    id: 'name',
    accessor: (item) => item.name,
    sortBy: 'alphanumeric',
  },
  {
    Header: translate('Deleted'),
    id: 'modified',
    customWidth: '160px',
    sortType: (a, b) =>
      sortByProperty('deletedDate', (val) => new Date(val).getTime())(
        a.original,
        b.original
      ),
    accessor: (workspaceItem: WorkspaceItem) =>
      xAgoFormatter({ date: workspaceItem.deletedDate }),
  },
];

export function WorkspaceDeleted({ workspaceId, itemId }: Props) {
  const router = useSocrativeRouter();
  const [parentId, setParentId] = React.useState<null | string>(null);

  React.useEffect(() => {
    setParentId(itemId);
  }, [itemId]);

  const [
    { open: featurePaywallOpen, touch: currentPaywallTouch },
    featurePaywallDispatch,
  ] = usePaywallReducer();
  const { data, workspaceQueries } = useWorkspaceItems({
    workspaceId: workspaceId,
    isDeleted: true,
  });
  const isLoading = workspaceQueries.some((query) => query.isLoading);

  const [{ isOpen, selectedItemCount, confirmAction }, purgeDialogDispatch] =
    usePurgeDialog();

  const {
    roleQuery,
    permissions: {
      restoreWorkspaceFolder: canRestoreFolder,
      restoreQuiz: canRestoreQuiz,
      purgeQuiz: canPurgeQuiz,
      purgeWorkspaceFolder: canPurgeFolder,
    },
  } = useWorkspacePermissionsV2({
    workspaceId,
  });

  const isViewerRole = roleQuery.data === WorkspaceRole.Viewer;
  const enableBulkActions = !(
    isViewerRole &&
    !canRestoreFolder &&
    !canRestoreQuiz &&
    !canPurgeQuiz &&
    !canPurgeFolder
  );

  const purgeWorkspaceItemAction = usePurgeWorkspaceItemAction({
    workspaceId: workspaceId,
    purgeDialogDispatch: purgeDialogDispatch,
    paywallCallback: () => {
      featurePaywallDispatch({
        type: 'OPEN',
        touch: [PaywallScreens.SharedLibraryListPurgeItem],
      });
    },
  });

  const restoreWorkspaceItemAction = useRestoreWorkspaceItemAction({
    workspaceId: workspaceId,
    paywallCallback: () => {
      featurePaywallDispatch({
        type: 'OPEN',
        touch: [PaywallScreens.SharedLibraryListRestoreItem],
      });
    },
  });

  const actions = React.useMemo(
    // Order matters
    () => [restoreWorkspaceItemAction, purgeWorkspaceItemAction],
    [purgeWorkspaceItemAction, restoreWorkspaceItemAction]
  );

  const handleSelection = useHandleWorkspaceItemSelection({
    setParentId: setParentId,
    router: router,
  });

  const parentIdFilters = useParentIdFilter({ parentId: parentId });

  const currentItem = data.find((t) => t.id === itemId);
  return (
    <div className="relative">
      <WorkspaceItemTable
        actions={actions}
        onSelection={handleSelection}
        filters={[parentIdFilters]}
        data={data}
        initialSort={{ id: 'name', desc: true }}
        workspaceId={workspaceId}
        emptyState={(data) => (
          <EmptyState data={data} isLoading={isLoading} itemId={itemId} />
        )}
        columns={columns}
        enableBulkActions={enableBulkActions}
      >
        <Container fillWidth row margin={'18px 0 0 0'} className={'py-1'}>
          <WorkspaceQuizzesBreadcrumbs
            currentItem={currentItem}
            workspaceId={workspaceId}
          />
        </Container>
        <OfficialLibraryBadge workspaceId={workspaceId} className="mb-2" />
      </WorkspaceItemTable>
      <PurgeDialog
        selectedItemCount={selectedItemCount}
        isDisabled={isLoading}
        confirmAction={confirmAction}
        isOpen={isOpen}
        onClose={() => purgeDialogDispatch({ type: 'close' })}
      />
      <SharedLibraryPaywall
        open={featurePaywallOpen}
        onClose={() => featurePaywallDispatch({ type: 'CLOSE' })}
        paywallScreen={currentPaywallTouch ? currentPaywallTouch[0] : null}
      />
    </div>
  );
}

function EmptyState({
  itemId,
  isLoading,
  data,
}: {
  itemId: string;
  isLoading: boolean;
  data: unknown[];
}) {
  if (isLoading) {
    return <Spinner />;
  }

  if (data.length > 0) {
    return null;
  }

  return (
    <div
      className="p-16 flex justify-center content-center text-md font-display text-blue-900"
      style={{ borderBottom: '1px solid rgba(0, 0, 0, 0.1)' }}
    >
      {itemId == null
        ? translate('The trash is empty')
        : translate('This folder is empty')}
    </div>
  );
}
